import { Card } from "@/components/ui/card";
import { GroupUserIcon, ThumbaCoin } from "@roman/shared/components/Icons";
import type { PredictionMarketWithProbability } from "@roman/shared/types/PredictionMarket";
import { formatNumberWithCommas } from "@roman/shared/utils/formatNumberWithCommas";
import { PredictionMarketType, getPredictionMarketType } from "@roman/shared/utils/predictionMarket/utils";
import { differenceInHours, isAfter } from "date-fns";
import { useNavigate } from "react-router-dom";
import { EntryShareButton } from "../EntryShareButton";
import { Tag } from "../Tag";
import { Title } from "./Title";

interface Props {
  market: PredictionMarketWithProbability & {
    totalEnteredFreePoints: number;
    numberOfParticipants: number;
  };
  choiceComponent: React.ReactNode;
}

export const MarketCardListItem = ({ market, choiceComponent }: Props) => {
  const navigate = useNavigate();
  const { id: marketId, title } = market;
  const predictionMarketType = getPredictionMarketType(market.predictionMarketChoices.map((c) => ({ name: c.name })));

  const shouldShowFinalLabel =
    market.entryCloseAt &&
    differenceInHours(new Date(market.entryCloseAt), new Date()) < 24 &&
    isAfter(new Date(market.entryCloseAt), new Date());

  return (
    <Card
      className="hover:shadow-lg transition-shadow duration-200 cursor-pointer flex flex-col relative overflow-hidden"
      onClick={() => navigate(`/prediction-market/details/${marketId}`)}
    >
      <div className="p-5 flex flex-col items-stretch">
        <div>
          <div className="flex justify-between items-center">
            <Tag text={market.subLabel} className="text-xs px-[10px] py-1.5 leading-3" />
            <div className="flex flex-col gap-1 text-xs text-gray-600 text-light-gray">
              <div className="flex flex-1 justify-between items-center gap-4" onClick={(e) => e.stopPropagation()}>
                <EntryShareButton market={market} className="text-dark-gray w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between pb-0 gap-[10px] mb-[16px] mt-[10px]">
            {market.primaryImageURL && (
              <img src={market.primaryImageURL} alt={title} className="object-cover rounded-lg h-[48px] w-[48px]" />
            )}
            <Title
              text={title}
              className="text-[17px] leading-5 line-clamp-2 font-bold text-ellipsis overflow-hidden"
            />
          </div>
        </div>

        <div className={`flex-shrink ${predictionMarketType === PredictionMarketType.TwoChoice ? "" : "h-[94px]"}`}>
          <div onClick={(e) => e.stopPropagation()}>{choiceComponent}</div>
        </div>

        <div className="flex items-center justify-between mt-[17px]">
          {market.numberOfParticipants > 0 && (
            <>
              <div className="flex items-center justify-center gap-[6px] h-[25px]">
                <ThumbaCoin className="w-[20px]" />
                <div className="text-[18px] font-roboto font-bold">
                  {formatNumberWithCommas(market.totalEnteredFreePoints)}
                </div>
              </div>
              <div className="flex items-center gap-[7px] text-dark-gray text-xs mr-0.5">
                <div className="flex items-center gap-[2.82px]">
                  <GroupUserIcon />
                </div>
                <span className="font-bold text-sm">{formatNumberWithCommas(market.numberOfParticipants)}</span>
              </div>
            </>
          )}
        </div>

        {market.numberOfParticipants === 0 && <NewTag />}
      </div>

      {shouldShowFinalLabel && <FinalTag />}
    </Card>
  );
};

const FinalTag = () => {
  return (
    <div className="absolute left-0 top-0 w-10 h-10 overflow-hidden">
      <div className="absolute inset-0 bg-transparent [clip-path:polygon(0_100%,100%_100%,100%_0)]" />

      <div className="absolute inset-0 bg-red [clip-path:polygon(0_0,0_100%,100%_0)]" />

      <div
        className="text-[10px] text-white absolute top-[7px] left-0 font-bold w-7"
        style={{
          transform: "rotate(-45deg)",
        }}
      >
        FINAL
      </div>
    </div>
  );
};

const NewTag = () => {
  return (
    <div className="rounded-[6px] text-secondary text-sm font-bold border border-secondary w-[54px] h-[26px] flex items-center justify-center">
      NEW
    </div>
  );
};
